import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaEdit,
  FaBan,
  FaPlus,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCheck,
  FaTrash,
  FaSearch,
  FaDownload,
} from "react-icons/fa";
import dayjs from "dayjs";
import UserFormModal from "./UserFormModal";
import UserViewModal from "./UserViewModal";
import Sidebar from "./Sidebar";
import { useNavigate } from 'react-router-dom';

const LeftArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
    />
  </svg>
);
const RightArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c12.5-12.5 12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"
    />
  </svg>
);

// --- PASTE THESE COMPONENTS AT THE BOTTOM OF UserMaster.js ---

const ActionModal = ({ isOpen, status, message, onClose }) => {
  if (!isOpen) return null;

  let bgColor = 'bg-blue-100';
  let textColor = 'text-blue-600';
  let title = 'Processing...';

  if (status === 'success') {
    bgColor = 'bg-green-100';
    textColor = 'text-green-600';
    title = 'Done';
  } else if (status === 'error') {
    bgColor = 'bg-red-100';
    textColor = 'text-grey-600';
    title = 'Error';
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity"></div>
      <div className="relative bg-white shadow-xl max-w-sm w-full mx-4 p-6 text-center rounded-lg">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${bgColor} mb-4`}>
          {status === 'loading' ? (
            <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          ) : status === 'success' ? (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
          ) : (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
          )}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className={`text-sm ${textColor} mb-6`}>{message}</p>
        {status !== 'loading' && (
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200">Close</button>
        )}
      </div>
    </div>
  );
};

const ConfirmationDialog = ({ isOpen, title, message, onConfirm, onCancel, isDanger = false }) => {
  if (!isOpen) return null;

  const btnClass = isDanger 
    ? 'bg-red-600 hover:bg-red-700 text-white' 
    : 'bg-teal-600 hover:bg-teal-700 text-white';

  return (
    <div className="fixed inset-0 z-[50] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" onClick={onCancel}></div>
      <div className="relative bg-white shadow-xl max-w-sm w-full mx-4 p-6 text-center rounded-lg">
        <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${isDanger ? 'bg-red-100' : 'bg-teal-100'} mb-4`}>
          {isDanger ? (
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
          ) : (
            <svg className="h-6 w-6 text-teal-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>
          )}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex justify-center gap-3">
          <button onClick={onCancel} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-medium rounded transition-colors ${btnClass}`}>Confirm</button>
        </div>
      </div>
    </div>
  );
};

const API_URL = "/api";
const UserMaster = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
   const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [role, setRole] = useState("All");
  const [status, setStatus] = useState("All");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
const [canCreate, setCanCreate] = useState(true);
  // States for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

   const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    isDanger: false
  });

  const [actionModal, setActionModal] = useState({
    isOpen: false,
    status: 'loading', // 'loading' | 'success' | 'error'
    message: ''
  });
const checkLicense = async () => {
  try {
    const res = await fetch(`${API_URL}/license/check`, {
      headers: getAuthHeaders()
    });

    const data = await res.json();

    setCanCreate(data.allowed); // backend should return { allowed: true/false }
  } catch (err) {
    console.error('License check failed:', err);
    setCanCreate(true); // fallback: allow (or false if you want strict)
  }
};
 const getAuthHeaders = (isFormData = false) => {
  const token = localStorage.getItem('authToken'); // Or 'authToken' depending on your login logic
  
  const headers = {
    // Only add Authorization if token exists
    ...(token && { 'Authorization': `Bearer ${token}` })
  };

  // Only set Content-Type to 'application/json' if we are NOT sending FormData.
  // If sending FormData, the browser MUST set the Content-Type and boundary automatically.
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

  // Function to map database role values to display values
  const getDisplayRole = (dbRole) => {
    switch (dbRole) {
      case "Admin":
        return "Admin";
      case "User":
        return "User";
      case "Manager":
        return "Manager";
      case "Vendor":
        return "Vendor";
      default:
        return dbRole || "Unknown";
    }
  };

  // Function to get role color for styling (updated color theme => teal / indigo / amber / slate)
  const getRoleColor = (role) => {
    switch (role) {
      case "Admin":
        return "bg-amber-50 text-amber-700";
      case "Manager":
        return "bg-teal-50 text-teal-700";
      case "Vendor":
        return "bg-indigo-50 text-indigo-700";
      case "User":
        return "bg-slate-50 text-slate-800";
      default:
        return "bg-slate-50 text-slate-800";
    }
  };

  // -------- THEME ONLY: apply DepartmentMaster color variables + scoped overrides --------
  useEffect(() => {
    // Same theme variables used in DepartmentMaster
    const themeVars = {
      "--sidebar-line-color": "#e6efee",
      "--sidebar-bg": "#ffffff",
      "--accent-color": "#2b8f85", // primary accent (mint/teal)
      "--accent-hover": "#23756e",
      "--page-border-color": "#e6efee",
      "--text-primary": "#334155",
      "--text-muted": "#475569",
      "--text-muted-2": "#64748b",
      "--disabled-color": "#bcd8d5",
      "--sort-inactive": "#cddfe0",
      "--search-icon": "#cbd5da",
      "--status-active-bg": "#e6f6f3",
      "--status-active-color": "#0f766e",
      "--status-inactive-bg": "#fafaf9",
      "--status-inactive-color": "#6b7280",
      "--page-bg": "#EEF6F5",
      "--table-header-bg": "#EEF6F5",
    };
    Object.entries(themeVars).forEach(([k, v]) =>
      document.documentElement.style.setProperty(k, v)
    );

    // Inject a small, scoped stylesheet for mapping a few Tailwind classes used in this file
    const styleId = "user-master-theme-overrides";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
       
        

        /* Table header uses the mint header background (Department style) */
        .user-master-root main table thead { background-color: var(--table-header-bg) !important; color: var(--text-primary) !important; }

        /* Pagination active/inactive styling */
        .user-master-root .page-active { background-color: var(--accent-color) !important; color: #ffffff !important; border-radius: 4px; padding: 0.25rem 0.5rem; }
        .user-master-root .page-inactive { color: var(--accent-color) !important; }
        .user-master-root .page-inactive:hover { background-color: #f7fbfa !important; }

        

        /* Status badge fallbacks (consistent with Department) */
        .user-master-root .status-active { background-color: var(--status-active-bg); color: var(--status-active-color); border-radius: 9999px; padding: 4px 10px; font-size: 12px; font-weight: 700; display: inline-block; }
        .user-master-root .status-inactive { background-color: var(--status-inactive-bg); color: var(--status-inactive-color); border-radius: 9999px; padding: 4px 10px; font-size: 12px; font-weight: 700; display: inline-block; }

        /* Ensure sticky header shadow contrast when scrolled */
        .user-master-root .shadow { box-shadow: 0 1px 0 rgba(15,23,42,0.04) !important; }
      `;
      document.head.appendChild(style);
    }
  }, []);
  // ---------------------------------------------------------------------------------------

  useEffect(() => {
    fetchUsers();
    checkLicense(); 
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, role, status]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/user`,{headers: getAuthHeaders()});
      const data = await res.json();
      setUsers(data || []);
    } catch {
      setUsers([]);
    }
    setLoading(false);
  };

   const handleLogout = () => {
    navigate('/login');
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    const sortedUsers = [...users].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      if (key === "CreatedAt" || key === "LastLogin") {
        return direction === "asc"
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      if (typeof a[key] === "string" && typeof b[key] === "string") {
        return direction === "asc"
          ? a[key].localeCompare(b[key])
          : b[key].localeCompare(a[key]);
      }
      return 0;
    });
    setUsers(sortedUsers);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 w-3 h-3 text-slate-400" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="ml-1 w-3 h-3 text-slate-800" />
    ) : (
      <FaSortDown className="ml-1 w-3 h-3 text-slate-800" />
    );
  };

  const handleView = (user) => {
    setSelectedUser(user);
    setShowView(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDeactivate = async (user) => {
    if (!window.confirm("Are you sure you want to deactivate this user?"))
       return;
    try {
      const response = await fetch(`${API_URL}/user/${user.UserID}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...user, IsActive: 0 }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to deactivate user");
      }
      fetchUsers();
      await checkLicense();
    } catch (error) {
      alert(error.message || "An error occurred while deactivating the user");
    }
  };

  const handleReactivate = async (user) => {
    if (!window.confirm("Are you sure you want to reactivate this user?")) 
      return;
    try {
      const response = await fetch(`${API_URL}/user/${user.UserID}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...user, IsActive: 1 }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to reactivate user");
      }
      fetchUsers();
      await checkLicense();
    } catch (error) {
      alert(error.message || "An error occurred while reactivating the user");
    }
  };

    // ---------------------------------------------------------
  // UPDATED HANDLE DELETE FUNCTION
  // ---------------------------------------------------------
   const handleDelete = (user) => {
    // 1. Open the Custom Confirmation Modal
    setConfirmModal({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to delete user "${user.FullName}"? This action cannot be undone.`,
      isDanger: true,
      onConfirm: async () => {
        // 2. Close the confirmation dialog
        setConfirmModal({ ...confirmModal, isOpen: false });

        // 3. Show Loading Modal
        setActionModal({ isOpen: true, status: 'loading', message: 'Deleting user...' });

        try {
          const response = await fetch(`${API_URL}/user/${user.UserID}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to delete user');
          }

          // 4. Refresh data (Use fetchUsers, NOT fetchSurveyBOQs)
          fetchUsers();
await checkLicense();
          // 5. Show Success Message
          setActionModal({
            isOpen: true,
            status: 'success',
            message: 'User deleted successfully'
          });

          // Auto-close success modal after 2 seconds
          setTimeout(() => setActionModal({ ...actionModal, isOpen: false }), 2000);

        } catch (error) {
          console.error('Error deleting:', error);

          // 6. Show Error Message
          setActionModal({
            isOpen: true,
            status: 'error',
            message: error.message || 'An error occurred while deleting the user'
          });
        }
      }
    });
  };
  const handleCreate = () => {
    setSelectedUser(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData, isEditMode) => {
    setShowForm(false);
    fetchUsers();
    await checkLicense();
  };

  const handleDownloadCSV = () => {
    const headers = [
      "Username",
      "Full Name",
      "Email",
      "Phone",
      "Role",
      "Status",
      "Created At",
    ];
    const rows = users.map((u) => [
      u.Username,
      u.FullName,
      u.Email,
      u.Phone,
      u.Role,
      u.IsActive ? "Active" : "Inactive",
      u.CreatedAt ? dayjs(u.CreatedAt).format("YYYY-MM-DD HH:mm") : "-",
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Filter users based on search term and filters
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      searchTerm === "" ||
      user.Username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.FullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.Email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.Phone &&
        user.Phone.toLowerCase().includes(searchTerm.toLowerCase()));
            const matchesRole = role === "All"  || (user.Role && user.Role.toLowerCase() === role.toLowerCase());;
    const matchesStatus =
      status === "All" ||
      (status === "Active" && user.IsActive) ||
      (status === "Inactive" && !user.IsActive);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const usersForPage = filteredUsers.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPaginationButtons = () => {
    const pages = [];
    // Show a max of 5 page buttons
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    // Render "previous" button
    pages.push(
      <button
        key="prev"
        className="p-1 hover:bg-slate-100 disabled:opacity-50 text-slate-800"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <LeftArrowIcon className="w-3 h-3 text-slate-800" />
      </button>
    );

    // Render page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`px-4 py-1 transition-colors ${
            currentPage === i
              ? "bg-teal-600 text-white font-semibold"
              : "text-slate-800 hover:bg-slate-100"
          }`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Render "next" button
    pages.push(
      <button
        key="next"
        className="p-1 hover:bg-slate-100 disabled:opacity-50 text-slate-800"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      >
        <RightArrowIcon className="w-3 h-3 text-slate-800" />
      </button>
    );

    return pages;
  };

  // Get unique roles for filter dropdown
  const roles = [...new Set(users.map((u) => u.Role).filter(Boolean))];

  // Theme object passed to Sidebar so it matches this page's accent/profile color
  const sidebarTheme = {
    text: 'var(--text-primary, #334155)',
    muted: 'var(--text-muted, #475569)',
    border: 'var(--page-border-color, #e6efee)',
    sidebarBorder: 'var(--sidebar-line-color, #e6efee)',
    background: 'var(--sidebar-bg, #ffffff)',
    pageBackground: 'var(--page-bg, #EEF6F5)',
    activeBg: 'var(--accent-color, #2b8f85)',
    profileBg: 'var(--accent-color, #2b8f85)',
    activeText: '#ffffff',
    accent: 'var(--accent-color, #2b8f85)',
    accentHover: 'var(--accent-hover, #23756e)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--accent-color, #2b8f85)',
    sidebarBg: 'var(--sidebar-bg, #ffffff)',
  };

  return (
    // added a harmless class "user-master-root" so theme overrides are scoped
    <div className="min-h-screen flex bg-slate-50 font-sans text-slate-800 user-master-root">
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={sidebarTheme} onLogout={handleLogout}
      />
      <main className={`flex-1 p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-300 pb-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                User Master
              </h1>
              <p className="text-slate-600">
                Manage your user accounts and access permissions
              </p>
            </div>
            <div className="flex items-center gap-3">

              <button
                onClick={handleDownloadCSV}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-800 hover:bg-slate-50 transition-all duration-200 flex items-center gap-2"
              >
                <FaDownload className="w-4 h-4" />
                Export
              </button>

              <button
  onClick={handleCreate}
  disabled={!canCreate}
  className={`bg-teal-600 text-white font-medium px-4 py-2 shadow transition-all duration-200 flex items-center gap-2
    ${!canCreate ? 'opacity-50 cursor-not-allowed bg-gray-400' : ''}`}
>
  <FaPlus className="w-4 h-4" />
  Create New
</button>
              
            </div>
          </div>
        </div>

        {/* Stats Cards (sharp edges like department master) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-white border border-slate-300 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Total Users</p>
                <p className="text-2xl font-bold text-slate-900">{users.length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-sky-50 text-sky-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-300 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Active Users</p>
                <p className="text-2xl font-bold text-slate-900">{users.filter((u) => u.IsActive).length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 text-emerald-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-300 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Inactive Users</p>
                <p className="text-2xl font-bold text-slate-900">{users.filter((u) => !u.IsActive).length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-rose-50 text-rose-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-300 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">This Month</p>
                <p className="text-2xl font-bold text-slate-900">
                  {
                    users.filter((u) => {
                      const createdDate = new Date(u.CreatedAt);
                      const now = new Date();
                      return (
                        createdDate.getMonth() === now.getMonth() &&
                        createdDate.getFullYear() === now.getFullYear()
                      );
                    }).length
                  }
                </p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-violet-50 text-violet-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters (sharp edges, vendor/dept style) */}
        <div className="p-6 bg-white border border-slate-200 shadow-sm mb-6" style={{ borderRadius: 0 }}>
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label className="block text-slate-700 text-sm font-medium mb-2">
                What are you looking for?
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search for username, name, email, etc"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-2">
                Role
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-48 py-2 px-3 bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 text-sm"
              >
                <option value="All">All</option>
                {roles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-2">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-48 py-2 px-3 bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 text-sm"
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <button className="bg-teal-600  text-white font-medium px-6 py-2 shadow transition-all duration-200 text-sm" onClick={() => setCurrentPage(1)}>
              Search
            </button>
          </div>
        </div>

        {/* Users Table (attached/flush style, sharp edges like DepartmentMaster) */}
        <div className="flex-1 overflow-hidden bg-white border border-slate-200 shadow-sm" style={{ borderRadius: 0 }}>
          <div className="flex justify-between items-center p-4 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900">User Summary</h2>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2 text-slate-700">
                {renderPaginationButtons()}
                <button
                  title="Download CSV"
                  className="p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-all duration-200"
                  onClick={handleDownloadCSV}
                >
                  <FaDownload />
                </button>
              </div>
            </div>
          </div>

          {/* table container */}
          <div className="h-full overflow-x-auto scroll-smooth">
            <table className="w-full text-sm text-center">
              <thead style={{ backgroundColor: 'var(--table-header-bg)', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: 12, fontWeight: 600 , textAlign: "center"}} >
                <tr>
                  <th
                    className={`px-3 py-3 text-center cursor-pointer sticky text-center top-0 z-20 ${isScrolled ? "shadow" : ""}`}
                    onClick={() => handleSort("Username")}
                  >
                    <div className="flex items-center justify-center text-center w-full">USERNAME {getSortIcon("Username")}</div>
                  </th>

                  <th
                    className={`px-4 py-3 text-center cursor-pointer sticky top-0 z-20 ${isScrolled ? "shadow" : ""}`}
                    onClick={() => handleSort("FullName")}
                  >
                    <div className="flex items-center justify-center text-center w-full">FULL NAME {getSortIcon("FullName")}</div>
                  </th>

                  <th
                    className={`px-4 py-3 text-center cursor-pointer sticky top-0 z-20 ${isScrolled ? "shadow" : ""}`}
                    onClick={() => handleSort("Email")}
                  >
                    <div className="flex items-center justify-center text-center w-full">EMAIL {getSortIcon("Email")}</div>
                  </th>

                  <th
                    className={`px-3 py-3 text-center cursor-pointer sticky top-0 z-20 ${isScrolled ? "shadow" : ""}`}
                    onClick={() => handleSort("Phone")}
                  >
                    <div className="flex items-center justify-center text-center w-full">PHONE NO. {getSortIcon("Phone")}</div>
                  </th>

                  <th
                    className={`px-4 py-3 text-center cursor-pointer sticky top-0 z-20 ${isScrolled ? "shadow" : ""}`}
                    onClick={() => handleSort("Role")}
                  >
                    <div className="flex items-center justify-center text-center w-full">ROLE {getSortIcon("Role")}</div>
                  </th>

                  <th
                    className={`px-3 py-3 text-center cursor-pointer sticky top-0 z-20 ${isScrolled ? "shadow" : ""}`}
                    onClick={() => handleSort("IsActive")}
                  >
                    <div className="flex items-center justify-center text-center w-full">STATUS {getSortIcon("IsActive")}</div>
                  </th>

                  <th
                    className={`px-3 py-3 text-center cursor-pointer sticky top-0 z-20 ${isScrolled ? "shadow" : ""}`}
                    onClick={() => handleSort("CreatedAt")}
                  >
                    <div className="flex items-center justify-center text-center w-full">CREATED AT {getSortIcon("CreatedAt")}</div>
                  </th>

                  <th className={`px-3 py-3 text-center sticky top-0 z-20 ${isScrolled ? "shadow" : ""}`}>ACTIONS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-left">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-slate-600 font-semibold">
                      Loading users...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-slate-600 font-semibold">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  usersForPage.map((user, idx) => (
                    <tr
                      key={user.UserID}
                      className={`transition-all duration-300 group hover:bg-slate-50 bg-white`}
                    >
                      <td className="px-6 py-4 text-left text-sm font-mono whitespace-nowrap">
                        <p className="text-teal-700 transition-all text-center duration-200 hover:underline" onClick={() => handleView(user)}>
                          {user.Username?.toUpperCase?.() || ""}
                        </p>
                      </td>

                      <td className="px-6 py-4 text-left text-sm text-center whitespace-nowrap font-semibold text-slate-900">
                        {user.FullName?.toUpperCase?.() || ""}
                      </td>
                      <td className="px-6 py-4 text-left text-sm text-center break-all max-w-[220px] text-slate-600">
                        {user.Email?.toUpperCase?.() || ""}
                      </td>
                      <td className="px-6 py-4 text-left text-sm text-center whitespace-nowrap text-slate-600">
                        {user.Phone || ""}
                      </td>
                      <td className="px-6 py-4 text-left text-sm text-center whitespace-nowrap text-slate-600">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm uppercase ${getRoleColor(user.Role)}`}>
                          {getDisplayRole(user.Role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-left text-sm">
                        <span className={`inline-block px-3 py-1 text-center rounded-full text-xs font-bold shadow-sm transition-all duration-200 uppercase ${user.IsActive ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}>
                          {user.IsActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-left text-sm text-center whitespace-nowrap text-slate-600">
                        {user.CreatedAt ? (
                          <div className="flex flex-col leading-tight uppercase">
                            <span>{dayjs(user.CreatedAt).format("DD MMM YYYY")}</span>
                            <span className="text-xs text-slate-500">{dayjs(user.CreatedAt).format("hh:mm")}</span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="px-6 py-4 flex gap-2 justify-center items-center">
                        <button title="View" className="p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-all duration-200 hover:scale-110" onClick={() => handleView(user)}>
                          <FaEye />
                        </button>
                        <button title="Edit" className="p-2 rounded-full text-teal-700 hover:text-teal-900 hover:bg-teal-50 transition-all duration-200 hover:scale-110" onClick={() => handleEdit(user)}>
                          <FaEdit />
                        </button>
                        {user.IsActive ? (
                          <button title="Deactivate" className="p-2 rounded-full hover:bg-rose-100 text-rose-600 transition-all duration-200 hover:scale-110" onClick={() => handleDeactivate(user)}>
                            <FaBan />
                          </button>
                        ) : (
                          <button title="Reactivate" className="p-2 rounded-full hover:bg-emerald-100 text-emerald-600 transition-all duration-200 hover:scale-110" onClick={() => handleReactivate(user)}>
                            <FaCheck />
                          </button>
                        )}
                        <button title="Delete" className="p-2 rounded-full hover:bg-rose-200 text-rose-700 transition-all duration-200 hover:scale-110" onClick={() => handleDelete(user)}>
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modals */}
        <UserFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedUser}
          isEdit={isEdit}
          loading={formLoading}
        />
        <UserViewModal
          open={showView}
          onClose={() => setShowView(false)}
          user={selectedUser}
        />
      </main>

            {/* ... existing modals (UserFormModal, UserViewModal) ... */}
      
      {/* --- ADD THESE MODAL RENDERS HERE --- */}
      <ActionModal 
        isOpen={actionModal.isOpen}
        status={actionModal.status}
        message={actionModal.message}
        onClose={() => setActionModal({ ...actionModal, isOpen: false })}
      />

      <ConfirmationDialog 
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        isDanger={confirmModal.isDanger}
      />
    </div>
  );
};

export default UserMaster;
